<template>
  <form
    :id="formId"
    :disabled="isLoading"
    class="credit-card-form__form"
    @submit.prevent="validateFields"
  >
    <div class="credit-card-form__columns">
      <input type="hidden" name="recurly-token" data-recurly="token" />
      <div class="credit-card-form__column">
        <h6 class="credit-card-form__column-title">Card Information</h6>
        <div
          v-for="(field, key) in cardFields"
          :key="key"
          class="credit-card-form__input-wrapper"
        >
          <input
            v-model="field.value"
            class="ine-input"
            :class="[fieldHasError(key) && 'credit-card-form__input--error']"
            :type="field.type"
            :placeholder="field.placeholder"
            :autocomplete="field.autocomplete"
            :data-recurly="field['data-recurly']"
            @focus="clearError(key)"
          />
          <Errors v-if="fieldHasError(key)" :errors="errors[key]" />
        </div>
        <div
          class="credit-card-form__input-wrapper credit-card-form__input-wrapper--full-width"
        >
          <div id="card-number"></div>
        </div>
        <div id="recurly-elements" class="recurly-elements">
          <div class="recurly-elements__label">Expiration Date</div>
          <div class="credit-card-form__input-wrapper">
            <div id="card-month"></div>
          </div>
          <span class="recurly-elements__separator">/</span>
          <div class="credit-card-form__input-wrapper">
            <div id="card-year"></div>
          </div>
          <div class="credit-card-form__input-wrapper">
            <div id="card-cvv"></div>
          </div>
        </div>
      </div>
      <div class="credit-card-form__column credit-card-form__column">
        <h6 class="credit-card-form__column-title">Billing Address</h6>
        <div
          v-for="(field, key) in billingFields"
          :key="key"
          class="credit-card-form__input-wrapper"
          :class="{
            'credit-card-form__input-wrapper--full-width': key === 'company',
          }"
        >
          <div v-if="key === 'country'">
            <Dropdown
              :class="[fieldHasError(key) && 'credit-card-form__input--error']"
              appendToBody
              searchable
              :options="contryDropdownOptions"
              :handleSelection="dropdownHandler"
              :placeholder="field.placeholder"
              :autocomplete="field.autocomplete"
            />
            <select
              v-model="field.value"
              hidden
              :type="field.type"
              :placeholder="field.placeholder"
              :autocomplete="field.autocomplete"
              :data-recurly="field['data-recurly']"
            >
              <option :value="field.value" selected>{{ field.label }}"</option>
            </select>
          </div>

          <input
            v-else
            v-model="field.value"
            class="ine-input"
            :class="[fieldHasError(key) && 'credit-card-form__input--error']"
            :type="field.type"
            :placeholder="field.placeholder"
            :autocomplete="field.autocomplete"
            :data-recurly="field['data-recurly']"
            @focus="clearError(key)"
          />
          <Errors v-if="fieldHasError(key)" :errors="errors[key]" />
        </div>
      </div>
    </div>
  </form>
</template>
<script>
import { clearFormErrors, validateForm } from '@/helpers'
import countryCodes from '@/static/countryCodes'
import Dropdown from '@/components/forms/elements/Dropdown.vue'
import Errors from '@/components/forms/elements/Errors.vue'

const contryDropdownOptions = countryCodes.map(({ name, value }) => ({
  label: name,
  code: value,
}))

const recurlyKey = process.env.VUE_APP_RECURLY_KEY

export default {
  name: 'AddCreditCardForm',
  components: {
    Dropdown,
    Errors,
  },
  props: {
    formId: {
      type: String,
      default: 'card-form',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      elements: '',
      contryDropdownOptions,
      errors: clearFormErrors(),
      cardFields: {
        'first name': {
          type: 'text',
          placeholder: 'First Name',
          rules: ['presence'],
          'data-recurly': 'first_name',
          value: '',
          autocomplete: 'given-name',
        },
        'last name': {
          type: 'text',
          placeholder: 'Last Name',
          'data-recurly': 'last_name',
          rules: ['presence'],
          value: '',
          autocomplete: 'family-name',
        },
      },
      billingFields: {
        'street address': {
          type: 'text',
          rules: ['presence'],
          placeholder: 'Street Address',
          'data-recurly': 'address1',
          value: '',
          autocomplete: 'street-address',
        },
        city: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'City',
          'data-recurly': 'city',
          value: '',
          autocomplete: 'locality',
        },
        state: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'State/Province',
          'data-recurly': 'state',
          value: '',
          autocomplete: 'region',
        },
        zip: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'Zip/Postal Code',
          'data-recurly': 'postal_code',
          value: '',
          autocomplete: 'postal-code',
        },
        country: {
          type: 'text',
          rules: ['presence'],
          placeholder: 'Country',
          'data-recurly': 'country',
          value: '',
          autocomplete: 'country',
        },
        phone: {
          type: 'tel',
          rules: ['presence'],
          placeholder: 'Phone Number',
          'data-recurly': 'phone',
          value: '',
          autocomplete: 'tel',
        },
        company: {
          type: 'text',
          rules: [],
          placeholder: 'Company',
          'data-recurly': 'company',
          value: '',
          autocomplete: 'organization',
        },
      },
    }
  },
  watch: {
    cardFields: {
      handler() {
        this.$emit('change')
      },
      deep: true,
    },
    billingFields: {
      handler() {
        this.$emit('change')
      },
      deep: true,
    },
  },
  created() {
    window.recurly.configure(recurlyKey)
    this.elements = window.recurly.Elements()
  },
  mounted() {
    this.attachRecurlyElements()
  },
  methods: {
    validateFields() {
      const { cardFields, billingFields } = this
      const { isValid, errors } = validateForm({
        ...cardFields,
        ...billingFields,
      })
      this.errors = errors
      if (isValid) {
        this.attemptCaptureToken()
      }
    },
    attemptCaptureToken() {
      const form = document.querySelector(`#${this.formId}`)
      window.recurly.token(this.elements, form, async (err, token) => {
        if (err) {
          this.$store.dispatch(
            'app/setError',
            `These card fields appear to be invalid: ${err.fields.join(', ')}`
          )
          this.sendMixpanelEvent(
            this.eventsConstants.SUBSCRIPTION_PAYMENT_RECURLY_ERROR,
            { error_fields: err.fields.join(', ') }
          )
        } else {
          this.$emit('handle-recurly-token', token)
        }
      })
    },
    attachRecurlyElements() {
      const style = {
        fontColor: '#999',
        fontSize: '12px',
        fontFamily: 'Roboto',
      }
      const exipartionDateStyle = {
        padding: '11px',
      }
      const cardNumberElement = this.elements.CardNumberElement({
        format: true,
        inputType: 'mobileSelect',
        style: {
          ...style,
          placeholder: {
            color: '#999',
            content: 'Card number',
          },
        },
      })
      const cardMonthElement = this.elements.CardMonthElement({
        format: true,
        inputType: 'select',
        style: {
          ...style,
          ...exipartionDateStyle,
          textAlign: 'center',
          placeholder: {
            color: '#999',
            content: 'MM',
          },
        },
      })
      const cardYearElement = this.elements.CardYearElement({
        inputType: 'select',
        style: {
          ...style,
          ...exipartionDateStyle,
          textAlign: 'center',
          placeholder: {
            color: '#999',
            content: 'YYYY',
          },
        },
      })
      const cardCvvElement = this.elements.CardCvvElement({
        inputType: 'mobileSelect',
        style: {
          ...style,
          textAlign: 'center',
          placeholder: {
            color: '#999',
            content: 'CVV',
          },
        },
      })
      cardNumberElement.attach('#card-number')
      cardMonthElement.attach('#card-month')
      cardYearElement.attach('#card-year')
      cardCvvElement.attach('#card-cvv')
    },
    clearError(field) {
      this.errors = Object.entries(this.errors).reduce((acc, [key, val], _) => {
        if (key !== field) {
          acc[key] = val
        }
        return acc
      }, {})
    },
    fieldHasError(field) {
      return !!(this.errors[field] && this.errors[field].length)
    },
    dropdownHandler(country) {
      this.billingFields.country.value = country.code
      if (country) this.clearError('country')
    },
  },
}
</script>
<style lang="scss">
.credit-card-form {
  &__columns {
    display: grid;
    gap: 20px;
  }
  &__column {
    width: 100%;
    @include mobile {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }

    &-title {
      font-size: rem(14);
      line-height: 20px;
      font-weight: 500;
      width: 100%;
      padding: 0;
      margin: 0 0 20px 0;
      color: var(--white);
      @include mobile {
        grid-column: 1 / span 2;
        margin: 0 0 15px 0;
      }
      a {
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
  &__input {
    &-wrapper {
      &--full-width {
        grid-column: 1 / span 2;
      }
    }
  }
}
.recurly-elements {
  display: flex;
  align-items: center;
  margin: -3px 0 18px 0;
  @include mobile {
    margin: 0 0 18px 0;
  }
  &__label {
    font-size: rem(12);
    line-height: 14px;
    color: var(--menu-subcategory);
    flex: 0 0 84px;
    display: flex;
    align-items: center;
    margin-right: 5px;
  }
  &__separator {
    color: var(--menu-subcategory);
    margin-left: 10px;
    font-size: rem(12);
    line-height: 14px;
  }
  .recurly-element {
    padding: 0;
    margin: 0;
  }
}
#card-cvv,
#card-year {
  width: 55px !important;
  margin-left: 10px;
}
#card-month {
  width: 44px !important;
}
</style>
